/**
 * ControlPanel Component
 * ----------------------
 * Studio Console Design: Compact toolbar with labeled controls
 * Includes metronome toggle and timing tolerance selector.
 */

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Play, SkipForward, RotateCcw, Volume2, VolumeX, Timer, Upload, Keyboard } from "lucide-react";
import type { Clef, KeySignature, TimeSignature, Difficulty, KeyboardRange } from "@/lib/exerciseGenerator";
import type { TolerancePreset } from "@/hooks/useMetronome";

interface ControlPanelProps {
  clef: Clef;
  keySignature: KeySignature;
  timeSignature: TimeSignature;
  difficulty: Difficulty;
  keyboardRange: KeyboardRange;
  tempoValue: string;
  isPlaying: boolean;
  soundEnabled: boolean;
  metronomeEnabled: boolean;
  tolerance: TolerancePreset;
  onClefChange: (clef: Clef) => void;
  onKeyChange: (key: KeySignature) => void;
  onTimeChange: (time: TimeSignature) => void;
  onDifficultyChange: (diff: Difficulty) => void;
  onKeyboardRangeChange: (range: KeyboardRange) => void;
  onTempoChange: (tempo: string) => void;
  onStart: () => void;
  onNext: () => void;
  onReset: () => void;
  onSoundToggle: () => void;
  onMetronomeToggle: () => void;
  onToleranceChange: (t: TolerancePreset) => void;
  onABRSMUpload?: () => void;
}

const DIFFICULTY_CONFIG: Record<Difficulty, { label: string; color: string }> = {
  1: { label: "ABRSM 1-2", color: "oklch(0.70 0.18 150)" },
  2: { label: "ABRSM 3", color: "oklch(0.82 0.17 75)" },
  3: { label: "ABRSM 4-5", color: "oklch(0.82 0.14 200)" },
  4: { label: "ABRSM 6", color: "oklch(0.75 0.18 30)" },
  5: { label: "ABRSM 7-8", color: "oklch(0.65 0.24 25)" },
};

const TOLERANCE_CONFIG: Record<TolerancePreset, { label: string; color: string; hint: string }> = {
  relaxed: { label: "Relaxed", color: "oklch(0.70 0.18 150)", hint: "±100ms perfect, ±350ms on-time" },
  normal:  { label: "Normal",  color: "oklch(0.82 0.14 200)", hint: "±60ms perfect, ±250ms on-time" },
  strict:  { label: "Strict",  color: "oklch(0.75 0.18 30)",  hint: "±35ms perfect, ±150ms on-time" },
};

export default function ControlPanel({
  clef,
  keySignature,
  timeSignature,
  difficulty,
  keyboardRange,
  tempoValue,
  isPlaying,
  soundEnabled,
  metronomeEnabled,
  tolerance,
  onClefChange,
  onKeyChange,
  onTimeChange,
  onDifficultyChange,
  onKeyboardRangeChange,
  onTempoChange,
  onStart,
  onNext,
  onReset,
  onSoundToggle,
  onMetronomeToggle,
  onABRSMUpload,
  onToleranceChange,
}: ControlPanelProps) {
  const diffConfig = DIFFICULTY_CONFIG[difficulty];
  const tolConfig = TOLERANCE_CONFIG[tolerance];

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Clef */}
      <ControlGroup label="Clef">
        <Select value={clef} onValueChange={(v) => onClefChange(v as Clef)}>
          <SelectTrigger className="h-7 w-[88px] text-xs border-border" style={{ background: "oklch(0.19 0.015 270)" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="treble">Treble</SelectItem>
            <SelectItem value="bass">Bass</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Key */}
      <ControlGroup label="Key">
        <Select value={keySignature} onValueChange={(v) => onKeyChange(v as KeySignature)}>
          <SelectTrigger className="h-7 w-[80px] text-xs border-border" style={{ background: "oklch(0.19 0.015 270)" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["C", "G", "D", "A", "E", "F", "Bb", "Eb", "Ab"] as KeySignature[]).map((k) => (
              <SelectItem key={k} value={k}>
                {k} Major
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Time */}
      <ControlGroup label="Time">
        <Select value={timeSignature} onValueChange={(v) => onTimeChange(v as TimeSignature)}>
          <SelectTrigger className="h-7 w-[68px] text-xs border-border" style={{ background: "oklch(0.19 0.015 270)" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="4/4">4/4</SelectItem>
            <SelectItem value="3/4">3/4</SelectItem>
            <SelectItem value="2/4">2/4</SelectItem>
            <SelectItem value="6/8">6/8</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Difficulty */}
      <ControlGroup label="Difficulty">
        <div className="flex items-center gap-1">
          {([1, 2, 3, 4, 5] as Difficulty[]).map((d) => (
            <button
              key={d}
              onClick={() => onDifficultyChange(d)}
              className="w-6 h-6 rounded text-[10px] font-mono font-bold transition-all"
              style={{
                background: d === difficulty ? DIFFICULTY_CONFIG[d].color : "oklch(0.19 0.015 270)",
                color: d === difficulty ? "oklch(0.14 0.015 270)" : "oklch(0.50 0.01 270)",
                border: `1px solid ${d === difficulty ? DIFFICULTY_CONFIG[d].color : "oklch(0.28 0.010 270)"}`,
                boxShadow: d === difficulty ? `0 0 8px ${DIFFICULTY_CONFIG[d].color}40` : "none",
              }}
            >
              {d}
            </button>
          ))}
          <span
            className="text-[10px] font-mono ml-1"
            style={{ color: diffConfig.color }}
          >
            {diffConfig.label}
          </span>
        </div>
      </ControlGroup>

      {/* Keyboard Range */}
      <ControlGroup label="Keyboard">
        <Select value={keyboardRange} onValueChange={(v) => onKeyboardRangeChange(v as KeyboardRange)}>
          <SelectTrigger className="h-7 w-[88px] text-xs border-border" style={{ background: "oklch(0.19 0.015 270)" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full (88)</SelectItem>
            <SelectItem value="61-key">61-key</SelectItem>
            <SelectItem value="49-key">49-key</SelectItem>
            <SelectItem value="37-key">37-key</SelectItem>
            <SelectItem value="25-key">25-key</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Tempo */}
      <ControlGroup label="Tempo">
        <Select value={tempoValue} onValueChange={onTempoChange}>
          <SelectTrigger className="h-7 w-[86px] text-xs border-border" style={{ background: "oklch(0.19 0.015 270)" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto</SelectItem>
            <SelectItem value="60">60 BPM</SelectItem>
            <SelectItem value="70">70 BPM</SelectItem>
            <SelectItem value="80">80 BPM</SelectItem>
            <SelectItem value="90">90 BPM</SelectItem>
            <SelectItem value="100">100 BPM</SelectItem>
            <SelectItem value="110">110 BPM</SelectItem>
            <SelectItem value="120">120 BPM</SelectItem>
            <SelectItem value="130">130 BPM</SelectItem>
            <SelectItem value="140">140 BPM</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Tolerance */}
      {metronomeEnabled && (
        <ControlGroup label="Timing">
          <Select value={tolerance} onValueChange={(v) => onToleranceChange(v as TolerancePreset)}>
            <SelectTrigger
              className="h-7 w-[90px] text-xs border-border"
              style={{ background: "oklch(0.19 0.015 270)" }}
              title={tolConfig.hint}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relaxed">Relaxed</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="strict">Strict</SelectItem>
            </SelectContent>
          </Select>
        </ControlGroup>
      )}

      {/* Divider */}
      <div
        className="w-px h-7 mx-1"
        style={{ background: "oklch(0.28 0.010 270)" }}
      />

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant={isPlaying ? "secondary" : "default"}
          className="h-7 px-3 text-xs gap-1.5 font-semibold"
          onClick={onStart}
        >
          <Play size={11} />
          {isPlaying ? "New" : "Start"}
        </Button>

        {isPlaying && (
          <Button size="sm" variant="secondary" className="h-7 px-3 text-xs gap-1.5" onClick={onNext}>
            <SkipForward size={11} />
            Skip
          </Button>
        )}

        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onReset} title="Reset session">
          <RotateCcw size={12} />
        </Button>

        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onSoundToggle} title={soundEnabled ? "Mute" : "Unmute"}>
          {soundEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
        </Button>

        {/* Metronome toggle */}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[10px] gap-1 font-mono"
          onClick={onMetronomeToggle}
          title={metronomeEnabled ? "Disable metronome" : "Enable metronome"}
          style={{
            color: metronomeEnabled ? "oklch(0.70 0.18 150)" : "oklch(0.45 0.01 270)",
            border: metronomeEnabled ? "1px solid oklch(0.70 0.18 150 / 30%)" : "1px solid oklch(0.28 0.010 270)",
          }}
        >
          <Timer size={11} />
          {metronomeEnabled ? "ON" : "OFF"}
        </Button>

        {/* ABRSM Upload */}
        {onABRSMUpload && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[10px] gap-1"
            onClick={onABRSMUpload}
            title="Import ABRSM cXML exercise"
          >
            <Upload size={11} />
            ABRSM
          </Button>
        )}
      </div>
    </div>
  );
}

function ControlGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="text-[8px] uppercase tracking-widest font-semibold"
        style={{ color: "oklch(0.42 0.01 270)" }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}
